import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setUnauthorizedHandler } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { AppLayout } from '@/components/AppLayout';

import LoginPage from '@/pages/LoginPage';
import OverviewPage from '@/pages/OverviewPage';

/**
 * Stránky s grafy se načítají líně.
 *
 * ECharts má přes 1 MB (≈350 kB gzip). Kdyby se importoval staticky, stáhl by se
 * i při otevření přehledu, který ho nepotřebuje — sparkline v tabulce kreslíme
 * ručně do canvasu právě proto. Overview je přitom vstupní stránka, na kterou
 * míří požadavek Lighthouse ≥ 85 (spec 08, akceptační kritérium 3).
 */
const PositionDetailPage = lazy(() => import('@/pages/PositionDetailPage'));
const ExperimentsPage = lazy(() => import('@/pages/ExperimentsPage'));
const ExperimentDetailPage = lazy(() => import('@/pages/ExperimentDetailPage'));
const ComparePage = lazy(() => import('@/pages/ComparePage'));
const DevicesPage = lazy(() => import('@/pages/DevicesPage'));
const ClickMapPage = lazy(() => import('@/pages/ClickMapPage'));
const GuardPage = lazy(() => import('@/pages/GuardPage'));
const GuardDetailPage = lazy(() => import('@/pages/GuardDetailPage'));
const KnowledgePage = lazy(() => import('@/pages/KnowledgePage'));
const AiPage = lazy(() => import('@/pages/AiPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const AdminAiPage = lazy(() => import('@/pages/AdminAiPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  // 401 z libovolného requestu → login, s návratovou adresou.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (location.pathname !== '/login') {
        navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, {
          replace: true,
        });
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, location]);

  if (isLoading) {
    return (
      <div className="app-boot">
        <div className="app-boot__spinner" aria-hidden="true" />
        <p>Načítám StickyIQ…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="*"
          element={
            <Navigate
              to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
              replace
            />
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route
        element={
          <Suspense fallback={<div className="skeleton skeleton--page" aria-label="Načítám…" />}>
            <AppLayout />
          </Suspense>
        }
      >
        <Route path="/" element={<OverviewPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/devices" element={<DevicesPage />} />
        <Route path="/clicks" element={<ClickMapPage />} />
        <Route path="/position/:id" element={<PositionDetailPage />} />
        <Route path="/experiments" element={<ExperimentsPage />} />
        <Route path="/experiments/:id" element={<ExperimentDetailPage />} />
        <Route path="/guard" element={<GuardPage />} />
        <Route path="/guard/placement/:id" element={<GuardDetailPage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/ai" element={<AiPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/ai" element={<AdminAiPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
